'use client';

/**
 * 路由级错误边界 — v6.97.62 P0 FIX 2026-08-03 老板报
 * 跟 global-error.tsx 区别: error.tsx 保留 root layout (顶部 nav / 底部 tab 栏) 还在
 * 触发: 路由组件 render 抛错时 (例如 /dongniai/exercise/xxx 内部组件 crash)
 *       global-error 是顶层 fallback, error.tsx 是路由级 fallback
 *
 * 行为:
 * 1) 显示中文友好 UI (不用 Next.js 默认英文 404 overlay)
 * 2) 自动 fetch /dongniai_client_error.php 落盘到 /tmp/dongniai-client-errors.log
 * 3) 提供"重试"和"返回首页"按钮
 *
 * 【v6.97.62+3 P0 FIX 2026-08-03 老板报】Build 错误修复
 * 真因: 之前用 <button onClick={() => reset()}> 在 JSX 直接写 inline function,
 *       Next.js 16 build prerender 阶段 serialize client component tree 时,
 *       onClick function 不能 stringify, 报 "Event handlers cannot be passed
 *       to Client Component props". 在 /knowledge-graph / /compare/... 等所有
 *       8 个 SEO page build 时都触发 (因为每个 page 加载整棵 component tree
 *       做 prerender).
 * 修法: 删所有 onClick inline function, 改用 useRef + useEffect 挂载后
 *       addEventListener 绑定 reset. 这样 JSX 里没有任何 inline function,
 *       build prerender 序列化时不会触发 stringify 错, 而 client side
 *       hydrate 后 useEffect 仍能正确绑定事件.
 */

import { useEffect, useRef } from 'react';

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const resetButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    // 落盘到 server log (PHP 端点 + 已有 client-error-tracker.js 兜底, 双保险)
    try {
      fetch('/dongniai_client_error.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          kind: 'route-error-boundary',
          message: error?.message || 'unknown',
          stack: error?.stack || '',
          url: typeof window !== 'undefined' ? window.location.href : '',
          userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
          digest: error?.digest || '',
          file: '',
          line: '',
          col: '',
        }),
        keepalive: true,
      }).catch(() => {});
    } catch {
      // 吞掉
    }

    // 用 useEffect 挂载后绑定 reset button, 避免 JSX 写 onClick inline function
    const btn = resetButtonRef.current;
    if (btn) {
      const handler = (e: MouseEvent) => {
        e.preventDefault();
        try {
          reset();
        } catch (e) {
          // reset 失败回退到 reload
          if (typeof window !== 'undefined') {
            window.location.reload();
          }
        }
      };
      btn.addEventListener('click', handler);
      return () => {
        btn.removeEventListener('click', handler);
      };
    }
  }, [error, reset]);

  return (
    <div
      style={{
        minHeight: '60vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          maxWidth: 480,
          width: '100%',
          background: '#fff',
          borderRadius: 16,
          padding: 32,
          boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
          textAlign: 'center',
          border: '1px solid #fee2e2',
        }}
      >
        <div
          style={{
            width: 56,
            height: 56,
            margin: '0 auto 12px',
            background: 'linear-gradient(135deg, #f59e0b, #ef4444)',
            borderRadius: 14,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 28,
            color: '#fff',
          }}
        >
          ⚠️
        </div>
        <h2 style={{ fontSize: 18, fontWeight: 600, margin: '0 0 8px', color: '#0f172a' }}>
          当前页面遇到问题
        </h2>
        <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 16px', lineHeight: 1.6 }}>
          页面加载时遇到异常。我们已自动记录该错误。
        </p>
        {error?.message && (
          <details
            style={{
              marginBottom: 16,
              padding: 10,
              background: '#fef3c7',
              border: '1px solid #fde68a',
              borderRadius: 6,
              fontSize: 11,
              color: '#78350f',
              textAlign: 'left',
              fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
              wordBreak: 'break-all',
            }}
          >
            <summary style={{ cursor: 'pointer', fontWeight: 500 }}>技术细节</summary>
            <div style={{ marginTop: 6, lineHeight: 1.5 }}>{error.message}</div>
          </details>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
          <button
            ref={resetButtonRef}
            type="button"
            style={{
              padding: '8px 16px',
              fontSize: 13,
              fontWeight: 500,
              color: '#fff',
              background: 'linear-gradient(135deg, #4F46E5, #7C3AED)',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
            }}
          >
            🔄 重试
          </button>
          <a
            href="/dongniai/"
            style={{
              padding: '8px 16px',
              fontSize: 13,
              fontWeight: 500,
              color: '#475569',
              background: '#fff',
              border: '1px solid #e2e8f0',
              borderRadius: 6,
              textDecoration: 'none',
              cursor: 'pointer',
            }}
          >
            🏠 返回首页
          </a>
        </div>
      </div>
    </div>
  );
}
